import { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

type DropdownType = "products" | "solution" | "rnd" | "about" | null;
type MobileMenuType = "products" | "solution" | "about" | null;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<DropdownType>(null);
  const [openMobileMenu, setOpenMobileMenu] = useState<MobileMenuType>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const solutionsContainerRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const activePage = location.pathname;

  const isWhiteBackgroundPage = [
    "/technology",
    "/certifications",
    "/contact",
  ].some((path) => activePage.startsWith(path));

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        navRef.current &&
        event.target instanceof Node &&
        !navRef.current.contains(event.target)
      ) {
        setOpenDropdown(null);
      }
      if (
        mobileMenuRef.current &&
        event.target instanceof Node &&
        !mobileMenuRef.current.contains(event.target) &&
        mobileMenuOpen
      ) {
        setMobileMenuOpen(false);
        setOpenMobileMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen]);

  useEffect(() => {
    const checkScrollButtons = () => {
      const container = solutionsContainerRef.current;
      if (container) {
        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(
          container.scrollLeft < container.scrollWidth - container.clientWidth
        );
      }
    };

    const container = solutionsContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollButtons);
      checkScrollButtons();
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollButtons);
      }
    };
  }, [openDropdown]);

  const scrollSolutions = (direction: "left" | "right") => {
    const container = solutionsContainerRef.current;
    if (container) {
      const scrollAmount = 480;
      const newScrollLeft =
        direction === "left"
          ? container.scrollLeft - scrollAmount
          : container.scrollLeft + scrollAmount;

      container.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
    }
  };

  const dropdownData = {
    products: {
      sections: [
        {
          title: "Consumer Battery",
          icon: "🔋",
          items: [
            "Lithium ion Battery",
            "Prismatic Cell",
          ],
        },
        {
          title: "Power Battery",
          icon: "⚡",
          items: [
            "Prismatic LFP Cell",
            "Prismatic Lithium Cells",
            "Module",
          ],
        },
        {
          title: "BMS",
          icon: "🔌",
          items: ["BMS"],
        },
      ],
    },
    solution: {
      categories: [
        {
          name: "BESS",
          image: "/solutions/bess.jpg",
          description: "Battery Energy Storage Systems",
        },
        {
          name: "EV Infrastructure",
          image: "/solutions/evinfrastructure.webp",
          description: "Electric Vehicle Charging Solutions",
        },
        {
          name: "Home",
          image: "/solutions/home.webp",
          description: "Residential Energy Solutions",
        },
        {
          name: "Ships",
          image: "/solutions/ships.jpg",
          description: "Marine & Naval Applications",
        },
        {
          name: "Tools",
          image: "/solutions/tools.jpg",
          description: "Industrial Power Tools",
        },
        {
          name: "Agriculture",
          image: "/solutions/agricultural.avif",
          description: "Farming & Agricultural Equipment",
        },
        {
          name: "Data Center",
          image: "/solutions/defense.png",
          description: "Data Center Power Solution",
        },
        {
          name: "UAV",
          image: "/solutions/uav.jpeg",
          description: "Unmanned Aerial Vehicles",
        },
      ],
    },
    rnd: {
      sections: [
        {
          title: "R&D Capability",
          icon: "🔬",
          items: [
            "Advanced Technology",
            "Advanced Manufacturing",
            "Quality Management",
          ],
        },
      ],
    },
    about: {
      sections: [
        {
          title: "About",
          icon: "ℹ️",
          items: ["Company Profile", "Social Responsibility"],
        },
      ],
    },
  };

  const getLinkColors = (isActive: boolean) => {
    if (isWhiteBackgroundPage && !scrolled) {
      return isActive
        ? "text-cyan-600 font-semibold"
        : "text-gray-900 hover:text-cyan-600";
    }

    if (scrolled) {
      return isActive
        ? "text-cyan-400 font-semibold"
        : "text-white/80 hover:text-cyan-400";
    }

    if (isWhiteBackgroundPage) {
      return isActive
        ? "text-cyan-600 font-semibold"
        : "text-gray-900 hover:text-cyan-600";
    }

    return isActive
      ? "text-cyan-400 font-semibold"
      : "text-white hover:text-white";
  };

  const getDropdownBg = () => {
    return "bg-stone-800 border border-white/10 shadow-2xl";
  };

  const getNavbarBackground = () => {
    if (scrolled) {
      return "bg-black/60 backdrop-blur-sm shadow-2xl border-b border-white/5";
    }

    return "bg-transparent backdrop-blur-xl";
  };

  const getTextColor = () => {
    if (isWhiteBackgroundPage && !scrolled) {
      return "text-gray-900";
    }

    if (scrolled) {
      return "text-white";
    }

    if (isWhiteBackgroundPage) {
      return "text-gray-900";
    }

    return "text-white";
  };

  const handleMouseEnter = (dropdown: DropdownType) => {
    setOpenDropdown(dropdown);
  };

  const handleMouseLeave = (event: React.MouseEvent) => {
    const relatedTarget = event.relatedTarget as Node | null;

    if (!relatedTarget || !(relatedTarget instanceof Node)) {
      setOpenDropdown(null);
      return;
    }

    if (navRef.current && !navRef.current.contains(relatedTarget)) {
      setOpenDropdown(null);
    }
  };

  const handleNavClick = (page: string) => {
    navigate(page);
    setOpenDropdown(null);
    setOpenMobileMenu(null);
    setMobileMenuOpen(false);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  const splitItemsIntoTwoColumns = (items: string[]) => {
    const mid = Math.ceil(items.length / 2);
    return [items.slice(0, mid), items.slice(mid)];
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (!mobileMenuOpen) {
      setOpenMobileMenu(null);
    }
  };

  const toggleMobileSubmenu = (menu: MobileMenuType) => {
    setOpenMobileMenu(openMobileMenu === menu ? null : menu);
  };

  return (
    <nav
      ref={navRef}
      className={`fixed w-full z-50 transition-all duration-500 ${getNavbarBackground()}`}
      onMouseLeave={handleMouseLeave}
    >
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-12 py-4 lg:py-5 flex items-center justify-between">
        <div
          className="flex items-center gap-3 group cursor-pointer"
          onClick={() => handleNavClick("/")}
        >
          <div className="text-2xl font-bold hover:opacity-80 transition-all duration-300 flex items-center gap-2">
            <img
              src={
                isWhiteBackgroundPage && !scrolled
                  ? "/logodark.png"
                  : "/logo.png"
              }
              alt="Devananta R&D Logo"
              className="w-12 h-8 group-hover:scale-110 transition-transform duration-500"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const nextSibling = e.currentTarget
                  .nextElementSibling as HTMLElement;
                if (nextSibling) {
                  nextSibling.classList.remove("hidden");
                }
              }}
            />
            <span className="hidden bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
              DA
            </span>
            <span
              className={`${
                isWhiteBackgroundPage && !scrolled
                  ? "text-gray-900"
                  : "bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent"
              }`}
            >
              Devananta R&D
            </span>
          </div>
        </div>

        <div
          className="hidden lg:flex items-center gap-1 absolute left-1/2 transform -translate-x-1/2"
          ref={dropdownRef}
        >
          <button
            onClick={() => handleNavClick("/")}
            onMouseEnter={() => setOpenDropdown(null)}
            className={`px-4 py-2 rounded-lg transition-all duration-300 text-sm font-medium relative group ${getLinkColors(
              activePage === "/"
            )}`}
          >
            Home
            {activePage === "/" && (
              <span
                className={`absolute bottom-0 left-0 w-full h-0.5 rounded-full ${
                  isWhiteBackgroundPage && !scrolled
                    ? "bg-gradient-to-r from-cyan-600 to-blue-600"
                    : "bg-gradient-to-r from-cyan-500 to-blue-500"
                }`}
              ></span>
            )}
          </button>

          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter("products")}
          >
            <button
              onClick={() => handleNavClick("/products")}
              className={`px-4 py-2 rounded-lg transition-all duration-300 text-sm font-medium flex items-center gap-1 relative group ${getLinkColors(
                activePage === "/products"
              )}`}
            >
              Products
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 ${
                  openDropdown === "products" ? "rotate-180" : ""
                } ${getTextColor()}`}
              />
              {activePage === "/products" && (
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 rounded-full ${
                    isWhiteBackgroundPage && !scrolled
                      ? "bg-gradient-to-r from-cyan-600 to-blue-600"
                      : "bg-gradient-to-r from-cyan-500 to-blue-500"
                  }`}
                ></span>
              )}
            </button>
            {openDropdown === "products" && (
              <div
                className={`absolute top-full left-full transform -translate-x-1/2 mt-4 w-[1170px] rounded-2xl overflow-hidden ${getDropdownBg()} animate-in fade-in slide-in-from-top-2 duration-300`}
              >
                <div className="p-8 grid grid-cols-3 gap-8">
                  {dropdownData.products.sections.map((section, idx) => (
                    <div key={idx} className="space-y-4">
                      <div className="flex items-center gap-3 pb-3 border-b border-cyan-500/20">
                        <span className="text-2xl">{section.icon}</span>
                        <h3 className="font-bold text-lg bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                          {section.title}
                        </h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        {splitItemsIntoTwoColumns(section.items).map(
                          (column, colIdx) => (
                            <ul key={colIdx} className="space-y-2.5">
                              {column.map((item, itemIdx) => (
                                <li
                                  key={itemIdx}
                                  className="text-sm pl-4 border-l-2 border-transparent hover:border-cyan-500 py-1.5 cursor-pointer transition-all duration-200 text-white hover:text-white hover:pl-6"
                                  onClick={() =>
                                    handleNavClick(
                                      `/products/${item
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")}`
                                    )
                                  }
                                >
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500"></div>
              </div>
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter("solution")}
          >
            <button
              onClick={() => handleNavClick("/solutions?solution=Home")}
              className={`px-4 py-2 rounded-lg transition-all duration-300 text-sm font-medium flex items-center gap-1 relative group ${getLinkColors(
                activePage === "/solutions"
              )}`}
            >
              Solution
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 ${
                  openDropdown === "solution" ? "rotate-180" : ""
                } ${getTextColor()}`}
              />
              {activePage === "/solutions" && (
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 rounded-full ${
                    isWhiteBackgroundPage && !scrolled
                      ? "bg-gradient-to-r from-cyan-600 to-blue-600"
                      : "bg-gradient-to-r from-cyan-500 to-blue-500"
                  }`}
                ></span>
              )}
            </button>
            {openDropdown === "solution" && (
              <div
                className={`absolute top-full left-24 transform -translate-x-1/2 mt-4 w-[98vw] max-w-7xl rounded-2xl overflow-hidden ${getDropdownBg()} animate-in fade-in slide-in-from-top-2 duration-300`}
              >
                <div className="p-7">
                  <div className="flex items-center">
                    <button
                      onClick={() => scrollSolutions("left")}
                      disabled={!canScrollLeft}
                      className={`flex-shrink-0 p-1 rounded-full transition-all duration-200 ${
                        canScrollLeft
                          ? "bg-white/10 text-white hover:bg-white/20 hover:scale-110 shadow-lg"
                          : "bg-white/5 text-white/30 cursor-not-allowed"
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    <div
                      ref={solutionsContainerRef}
                      className="flex-1 flex gap-2 overflow-x-auto scrollbar-hide px-1"
                      style={{
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                      }}
                    >
                      {dropdownData.solution.categories.map((category, idx) => (
                        <div
                          key={idx}
                          className="group cursor-pointer text-center flex-shrink-0 w-56"
                          onClick={() =>
                            handleNavClick(
                              `/solutions?solution=${encodeURIComponent(
                                category.name
                              )}`
                            )
                          }
                        >
                          <div className="w-44 h-28 mx-auto mb-1 rounded-2xl overflow-hidden shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300 border border-slate-200">
                            <img
                              src={category.image}
                              alt={category.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                const parent = e.currentTarget.parentElement;
                                if (parent) {
                                  parent.classList.add(
                                    "bg-gradient-to-br",
                                    "from-cyan-500",
                                    "to-blue-500",
                                    "flex",
                                    "items-center",
                                    "justify-center"
                                  );
                                  const fallbackText =
                                    document.createElement("div");
                                  fallbackText.className =
                                    "text-white text-xs font-semibold";
                                  fallbackText.textContent =
                                    category.name.substring(0, 3);
                                  parent.appendChild(fallbackText);
                                }
                              }}
                            />
                          </div>

                          <h3 className="font-semibold text-sm mb-1 text-white group-hover:text-cyan-500 transition-colors duration-200">
                            {category.name}
                          </h3>

                          <p className="text-xs text-white/70 group-hover:text-cyan-400 transition-colors duration-200 leading-tight">
                            {category.description}
                          </p>

                          <div className="w-0 group-hover:w-6 h-0.5 bg-cyan-500 mx-auto mt-2 transition-all duration-300 rounded-full"></div>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => scrollSolutions("right")}
                      disabled={!canScrollRight}
                      className={`flex-shrink-0 p-1 rounded-full transition-all duration-200 ${
                        canScrollRight
                          ? "bg-white/10 text-white hover:bg-white/20 hover:scale-110 shadow-lg"
                          : "bg-white/5 text-white/30 cursor-not-allowed"
                      }`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 mt-1 rounded-full"></div>
              </div>
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter("rnd")}
          >
            <button
              onClick={() => handleNavClick("/rnd")}
              className={`px-4 py-2 rounded-lg transition-all duration-300 text-sm font-medium flex items-center gap-1 relative group ${getLinkColors(
                activePage === "/rnd"
              )}`}
            >
              R&D
              {activePage === "/rnd" && (
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 rounded-full ${
                    isWhiteBackgroundPage && !scrolled
                      ? "bg-gradient-to-r from-cyan-600 to-blue-600"
                      : "bg-gradient-to-r from-cyan-500 to-blue-500"
                  }`}
                ></span>
              )}
            </button>
          </div>

          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter("about")}
          >
            <button
              onClick={() => handleNavClick("/about")}
              className={`px-4 py-2 rounded-lg transition-all duration-300 text-sm font-medium flex items-center gap-1 relative group ${getLinkColors(
                activePage === "/about"
              )}`}
            >
              About
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 ${
                  openDropdown === "about" ? "rotate-180" : ""
                } ${getTextColor()}`}
              />
              {activePage === "/about" && (
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 rounded-full ${
                    isWhiteBackgroundPage && !scrolled
                      ? "bg-gradient-to-r from-cyan-600 to-blue-600"
                      : "bg-gradient-to-r from-cyan-500 to-blue-500"
                  }`}
                ></span>
              )}
            </button>
            {openDropdown === "about" && (
              <div
                className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-4 w-[230px] rounded-2xl overflow-hidden ${getDropdownBg()} animate-in fade-in slide-in-from-top-2 duration-300`}
              >
                <div className="p-8">
                  {dropdownData.about.sections.map((section, idx) => (
                    <div key={idx} className="space-y-4">
                      <div className="flex items-center gap-3 pb-3 border-b border-cyan-500/20">
                        <h3 className="font-bold text-lg bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                          {section.title}
                        </h3>
                      </div>
                      <ul className="space-y-2.5">
                        {section.items.map((item, itemIdx) => (
                          <li
                            key={itemIdx}
                            className="text-sm pl-4 border-l-2 border-transparent hover:border-cyan-500 py-1.5 cursor-pointer transition-all duration-200 text-white hover:text-white hover:pl-6"
                            onClick={() => handleNavClick(`/about`)}
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500"></div>
              </div>
            )}
          </div>
        </div>

        <div className="hidden lg:flex items-center">
          <button
            onClick={() => handleNavClick("/contact")}
            className={`px-6 py-2.5 rounded-full transition-all duration-300 text-sm font-semibold relative overflow-hidden group ${
              activePage === "/contact"
                ? "bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/50"
                : "bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-lg hover:shadow-cyan-500/50 hover:scale-105"
            }`}
          >
            <span className="relative z-10">CONTACT US</span>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-4">
          <button
            onClick={toggleMobileMenu}
            className={`p-2 rounded-lg hover:bg-white/10 transition-colors ${getTextColor()}`}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="lg:hidden fixed inset-x-0 top-16 z-40 bg-black/95 backdrop-blur-xl shadow-2xl"
          style={{ height: "calc(100vh - 64px)" }}
        >
          <div className="h-full overflow-y-auto">
            <div className="px-4 py-6 space-y-1">
              <button
                onClick={() => handleNavClick("/")}
                className={`w-full text-left px-4 py-4 rounded-lg transition-all duration-300 text-base font-medium flex items-center justify-between ${
                  activePage === "/"
                    ? "bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border border-cyan-500/30"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                <span>Home</span>
                {activePage === "/" && (
                  <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                )}
              </button>

              <div className="space-y-1">
                <button
                  onClick={() => toggleMobileSubmenu("products")}
                  className={`w-full text-left px-4 py-4 rounded-lg transition-all duration-300 text-base font-medium flex items-center justify-between ${
                    activePage.startsWith("/products")
                      ? "bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border border-cyan-500/30"
                      : "text-white/90 hover:bg-white/10"
                  }`}
                >
                  <span>Products</span>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform duration-300 ${
                      openMobileMenu === "products" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openMobileMenu === "products" && (
                  <div className="ml-4 mt-2 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                    {dropdownData.products.sections.map((section, idx) => (
                      <div key={idx} className="space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{section.icon}</span>
                          <h3 className="font-semibold text-white/80">
                            {section.title}
                          </h3>
                        </div>
                        <ul className="space-y-2 ml-10">
                          {section.items.map((item, itemIdx) => (
                            <li key={itemIdx}>
                              <button
                                onClick={() =>
                                  handleNavClick(
                                    `/products/${item
                                      .toLowerCase()
                                      .replace(/\s+/g, "-")}`
                                  )
                                }
                                className="text-sm text-white/70 hover:text-cyan-400 transition-colors duration-200 py-1.5 w-full text-left"
                              >
                                {item}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <button
                  onClick={() => toggleMobileSubmenu("solution")}
                  className={`w-full text-left px-4 py-4 rounded-lg transition-all duration-300 text-base font-medium flex items-center justify-between ${
                    activePage.startsWith("/solution")
                      ? "bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border border-cyan-500/30"
                      : "text-white/90 hover:bg-white/10"
                  }`}
                >
                  <span>Solution</span>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform duration-300 ${
                      openMobileMenu === "solution" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openMobileMenu === "solution" && (
                  <div className="ml-4 mt-2 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="grid grid-cols-1 gap-3">
                      {dropdownData.solution.categories.map((category, idx) => (
                        <button
                          key={idx}
                          onClick={() =>
                            handleNavClick(
                              `/solutions?solution=${encodeURIComponent(
                                category.name
                              )}`
                            )
                          }
                          className="group text-left p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-300 border border-white/10 hover:border-cyan-500/50 flex items-center gap-3"
                        >
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
                            <img
                              src={category.image}
                              alt={category.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                const parent = e.currentTarget.parentElement;
                                if (parent) {
                                  parent.classList.add(
                                    "bg-gradient-to-br",
                                    "from-cyan-500",
                                    "to-blue-500",
                                    "flex",
                                    "items-center",
                                    "justify-center"
                                  );
                                  const fallbackText =
                                    document.createElement("div");
                                  fallbackText.className =
                                    "text-white text-xs font-semibold";
                                  fallbackText.textContent =
                                    category.name.substring(0, 3);
                                  parent.appendChild(fallbackText);
                                }
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-sm text-white group-hover:text-cyan-400 transition-colors">
                              {category.name}
                            </h3>
                            <p className="text-xs text-white/60 mt-1">
                              {category.description}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => handleNavClick("/rnd")}
                className={`w-full text-left px-4 py-4 rounded-lg transition-all duration-300 text-base font-medium flex items-center justify-between ${
                  activePage.startsWith("/rnd")
                    ? "bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border border-cyan-500/30"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                <span>R&D</span>
                {activePage.startsWith("/rnd") && (
                  <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                )}
              </button>

              <div className="space-y-1">
                <button
                  onClick={() => toggleMobileSubmenu("about")}
                  className={`w-full text-left px-4 py-4 rounded-lg transition-all duration-300 text-base font-medium flex items-center justify-between ${
                    activePage.startsWith("/about")
                      ? "bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border border-cyan-500/30"
                      : "text-white/90 hover:bg-white/10"
                  }`}
                >
                  <span>About</span>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform duration-300 ${
                      openMobileMenu === "about" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openMobileMenu === "about" && (
                  <div className="ml-4 mt-2 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                    {dropdownData.about.sections.map((section, idx) => (
                      <div key={idx} className="space-y-3">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-white/80">
                            {section.title}
                          </h3>
                        </div>
                        <ul className="space-y-2 ml-10">
                          {section.items.map((item, itemIdx) => (
                            <li key={itemIdx}>
                              <button
                                onClick={() => handleNavClick(`/about`)}
                                className="text-sm text-white/70 hover:text-cyan-400 transition-colors duration-200 py-1.5 w-full text-left"
                              >
                                {item}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-6 mt-4 border-t border-white/10">
                <button
                  onClick={() => handleNavClick("/contact")}
                  className="w-full px-4 py-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold text-base hover:shadow-lg hover:shadow-cyan-500/50 hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <span>CONTACT US</span>
                  <div className="w-4 h-4 rounded-full bg-white/20"></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
